import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  MapPin,
  Box,
  Anchor,
} from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type { AllocationTableRow, AllocationStatus, PositionType } from '@/types/allocations'
import {
  ALLOCATION_STATUS_LABELS,
  ALLOCATION_STATUSES,
  ALLOCATION_STATUS_VARIANTS,
  POSITION_TYPE_LABELS,
  POSITION_TYPES,
  POSITION_TYPE_VARIANTS,
  formatPosition,
  formatRotation,
} from '@/types/allocations'

interface AllocationsTableProps {
  data: AllocationTableRow[]
  isLoading: boolean
  onEdit: (id: string) => void
  onDelete: (id: string, tailNumber?: string) => void
}

/** Icon mapping for position type */
const positionTypeIcons: Record<PositionType, React.ComponentType<{ className?: string }>> = {
  hangar: MapPin,
  tiedown: Anchor,
  ramp: Box,
  unknown: MapPin,
}

/** Status filter options with icons */
const statusFilterOptions = ALLOCATION_STATUSES.map((status) => ({
  label: ALLOCATION_STATUS_LABELS[status],
  value: status,
}))

/** Position type filter options */
const positionTypeFilterOptions = POSITION_TYPES.map((type) => ({
  label: POSITION_TYPE_LABELS[type],
  value: type,
}))

export function AllocationsTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: AllocationsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<AllocationTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'aircraft',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Aircraft" />
      ),
      cell: ({ row }) => {
        const tailNumber = row.original.tail_number
        const typeName = row.original.aircraft_type_name

        return (
          <div>
            <div className="font-mono font-medium">{tailNumber || 'Unknown'}</div>
            {typeName && <div className="text-xs text-muted-foreground">{typeName}</div>}
          </div>
        )
      },
    },
    {
      id: 'location',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Location" />
      ),
      cell: ({ row }) => {
        const locationName = row.original.location_name
        return (
          <span className="font-medium">{locationName || '-'}</span>
        )
      },
    },
    {
      id: 'space',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Space" />
      ),
      cell: ({ row }) => {
        const buildingName = row.original.building_name
        const unitCode = row.original.unit_code

        if (buildingName || unitCode) {
          return (
            <div>
              {buildingName && <div className="text-sm font-medium">{buildingName}</div>}
              {unitCode && <div className="text-xs text-muted-foreground">{unitCode}</div>}
            </div>
          )
        }

        return <span className="text-muted-foreground">-</span>
      },
    },
    {
      accessorKey: 'position_type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Position Type" />
      ),
      cell: ({ row }) => {
        const positionType = row.getValue('position_type') as PositionType
        const Icon = positionTypeIcons[positionType]

        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <Badge variant={POSITION_TYPE_VARIANTS[positionType]}>
              {POSITION_TYPE_LABELS[positionType]}
            </Badge>
          </div>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'position',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Position" />
      ),
      cell: ({ row }) => {
        const x = row.original.position_x
        const y = row.original.position_y
        const z = row.original.position_z

        return <span className="text-xs font-mono">{formatPosition(x, y, z)}</span>
      },
    },
    {
      accessorKey: 'rotation',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Rotation" />
      ),
      cell: ({ row }) => {
        const rotation = row.getValue('rotation') as number
        return <span className="text-xs">{formatRotation(rotation)}</span>
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as AllocationStatus
        return (
          <Badge variant={ALLOCATION_STATUS_VARIANTS[status]}>
            {ALLOCATION_STATUS_LABELS[status]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const allocation = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(allocation.id)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(allocation.id, allocation.tail_number || undefined)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const search = filterValue.toLowerCase()
      const tailNumber = row.original.tail_number?.toLowerCase() || ''
      const label = row.original.custom_label?.toLowerCase() || ''
      const location = row.original.location_name?.toLowerCase() || ''

      return (
        tailNumber.includes(search) ||
        label.includes(search) ||
        location.includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
  })

  return (
    <div className="space-y-4">
      <DataTableToolbar
        table={table}
        searchPlaceholder="Search by aircraft, label, location..."
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        facetedFilters={[
          {
            column: table.getColumn('status'),
            title: 'Status',
            options: statusFilterOptions,
          },
          {
            column: table.getColumn('position_type'),
            title: 'Position Type',
            options: positionTypeFilterOptions,
          },
        ]}
      />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="h-10">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {isLoading ? 'Loading allocations...' : 'No allocations found.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  )
}
